// The chart's connecting lines as plain path data, shared by the on-screen chart (which culls
// to the visible area) and image/PDF/HTML exports (which draw everything).

import type { ParentType } from '../../model/types';
import { CARD_W, type Layout } from './layout';

export interface EdgePath {
  key: string;
  d: string;
  /** CSS classes, e.g. "edge partner ended" or "edge adoptive irregular". */
  cls: string;
  /** World-space bounds, for culling. */
  box: [number, number, number, number];
}

export function lineClass(types: ParentType[]): string {
  const t = types.find((x) => x !== 'biological' && x !== 'unknown');
  return t ? `edge ${t}` : 'edge';
}

export function edgePaths(layout: Layout): EdgePath[] {
  const N = layout.nodes;
  const CARD_H = layout.cardH;
  const out: EdgePath[] = [];

  for (const pe of layout.partners) {
    const a = N.get(pe.a)!;
    const b = N.get(pe.b)!;
    const ended = pe.rel.status === 'divorced' || pe.rel.status === 'ended' || pe.rel.status === 'separated';
    const cls = `edge partner${ended ? ' ended' : ''}`;
    const [l, r] = a.x < b.x ? [a, b] : [b, a];
    const box: EdgePath['box'] = [l.x, Math.min(l.y, r.y) - 60, r.x, Math.max(l.y, r.y) + CARD_H];
    if (pe.adjacent) {
      out.push({ key: pe.rel.id, cls, box, d: `M${l.x + CARD_W / 2},${l.y + CARD_H / 2} L${r.x - CARD_W / 2},${r.y + CARD_H / 2}` });
    } else {
      const h = 34 + Math.min(60, Math.abs(r.x - l.x) / 12);
      out.push({ key: pe.rel.id, cls, box, d: `M${l.x},${l.y} C${l.x},${l.y - h} ${r.x},${r.y - h} ${r.x},${r.y}` });
    }
  }

  layout.families.forEach((f, i) => {
    const ps = f.parents.map((p) => N.get(p)!);
    const cs = f.children.map((c) => ({ n: N.get(c.id)!, types: c.types }));
    if (f.regular) {
      // Drop points: one from the middle of an adjacent couple, otherwise one per parent.
      const drops: { x: number; y: number }[] = [];
      const used = new Set<number>();
      for (let a = 0; a < ps.length; a++) {
        if (used.has(a)) continue;
        const partnerIdx = ps.findIndex(
          (q, bIdx) => bIdx !== a && !used.has(bIdx) && layout.partners.some((pe) => pe.adjacent && ((pe.a === ps[a].id && pe.b === q.id) || (pe.b === ps[a].id && pe.a === q.id))),
        );
        if (partnerIdx >= 0) {
          used.add(a).add(partnerIdx);
          drops.push({ x: (ps[a].x + ps[partnerIdx].x) / 2, y: ps[a].y + CARD_H / 2 });
        } else {
          used.add(a);
          drops.push({ x: ps[a].x, y: ps[a].y + CARD_H });
        }
      }
      const xs = [...drops.map((d) => d.x), ...cs.map((c) => c.n.x)];
      const lo = Math.min(...xs);
      const hi = Math.max(...xs);
      const top = Math.min(...drops.map((d) => d.y));
      const bottom = Math.max(...cs.map((c) => c.n.y));
      const box: EdgePath['box'] = [lo, top, hi, bottom];
      out.push({ key: `f${i}`, cls: 'edge', box, d: drops.map((d) => `M${d.x},${d.y} V${f.busY}`).join(' ') + ` M${lo},${f.busY} H${hi}` });
      for (const c of cs) out.push({ key: `f${i}-${c.n.id}`, cls: lineClass(c.types), box, d: `M${c.n.x},${f.busY} V${c.n.y}` });
    } else {
      for (const p of ps)
        for (const c of cs) {
          const down = c.n.y > p.y;
          const y1 = down ? p.y + CARD_H : p.y;
          const y2 = down ? c.n.y : c.n.y + CARD_H;
          const my = (y1 + y2) / 2;
          out.push({
            key: `i${i}-${p.id}-${c.n.id}`,
            cls: `${lineClass(c.types)} irregular`,
            box: [Math.min(p.x, c.n.x), Math.min(y1, y2), Math.max(p.x, c.n.x), Math.max(y1, y2)],
            d: `M${p.x},${y1} C${p.x},${my} ${c.n.x},${my} ${c.n.x},${y2}`,
          });
        }
    }
  });
  return out;
}
